import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { SelectComponent, SelectOption, SelectConfig } from '../../../@core/components/ui/select';
import { DatepickerComponent } from '../../../@core/components/ui/datepicker';
import { InputComponent } from '../../../@core/components/ui/input/input.component';
import { IAttachment, IFile, IMilestone, IShareUser, IUser, MILESTONE_TYPE, SHARE_ACCESS } from '../../../@core/models/model';
import { FILE_TYPE, USER_ROLE, USER_TYPE } from '../../../@core/models/enums';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';
import { IDocumentType } from '../../../contract-management/models/settings.model';
import { DOCUMENT_CREATION_SOURCE, ContractService, IGenerateWithAIBody } from '../../../contract-management/services/contracts.service';
import { DocumentTypeService } from '../../../contract-management/services/document-type.service';
import { TemplateLibraryService } from '../../../contract-management/services/template-library.service';
import { CaseType } from '../../../@shared/services/case-format.service';
import { AssetUrlPipe } from "../../pipes/asset-url.pipe";
import { Observable, of } from 'rxjs';
import { ITabPanelConfig, TabsPanelsComponent } from '../../../@core/components/ui/tabs-panels/tabs-panels.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { IconComponent, IconSourceEnum } from '../../../@core/components/ui/icon/icon.component';
import { FileSelectionComponent } from '../file-selection/file-selection.component';
import {
  FileSelectionConfig,
  DropdownOption,
  FileItem,
  FileSelectionChangeEvent,
  FileSelectionSubmitEvent,
  FetchFunction,
  TabType,
} from '../file-selection/file-selection.model';
import { FileDropTypes } from '../file-drop/file-drop.types';
import { KnowledgeBaseService } from '../../../contract-management/services/knowledgebase.service';
import { ProjectService } from '../../../contract-management/services/project.service';
import { RepositoryService } from '../../../contract-management/services/repository.service';
import { CONTRACT_TYPE, DOCUMENT_MODEL, KNOWLEDGE_SCOPE } from '../../../contract-management/models/contract-management.model';
import { getFileType } from '../../utils/utils';
import { MenuComponent, CustomMenuItem, MenuItemType } from '../../../@core/components/ui/menu/menu.component';
import { map, catchError } from 'rxjs/operators';
import { StepperComponent } from '../stepper/stepper.component';
import { StepperStepDirective } from '../stepper-layout/stepper-step.directive';
import { StepperConfig, StepperStepConfig } from '../stepper-layout/stepper.types';
import { MilestonesComponent } from '../../../contract-management/components/milestones/milestones.component';
import { ShareToUsersComponent } from '../share-to-users/share-to-users.component';
import { GENERAL_AGENT_SVG, GENERATE_WITH_AI_SVGS } from 'src/app/common/svgs';
import { DocumentPreviewComponent } from "../document-preview/document-preview.component";
import { CLAUSE_STATUS, ClauseLibraryService, IClause } from 'src/app/contract-management/services/clause-library.service';
import { ClauseBundleService, IClauseBundle } from 'src/app/contract-management/services/clause-bundle.service';
import { CheckboxComponent } from "src/app/@core/components/ui/checkbox";
import { MileStoneService } from 'src/app/contract-management/services/milestone.service';
import { IntegrationsService, MailMessageInfo } from '../../../contract-management/services/integrations.service';
import { CHAT_STYLES, ChatAttachment, QueryOptions } from 'src/app/features/general-agent/general-agent-types';
import { CONNECTOR_PROVIDER_INFO, CONNECTOR_PROVIDERS, TOOLKIT_PROVIDERS } from 'src/app/features/general-agent/constants/tool.constants';
import { AttachmentGroupComponent } from 'src/app/@shared/components/attachment-group/attachment-group.component';
import { GeneralAgentService } from 'src/app/features/general-agent/services/general-agent.service';
import { AttachmentFilterChip } from '../attachment-group/attachment-group-filter-types';

const ALL_BUNDLES_VALUE = '';
const ALL_KNOWLEDGE_FOLDER_PREFIX = 'all-knowledge-';
const INTEGRATIONS_TAB_ID = 'INTEGRATIONS';
const INTEGRATIONS_TOOLKIT_PROVIDERS = [
  TOOLKIT_PROVIDERS.GOOGLE_DRIVE,
  TOOLKIT_PROVIDERS.ONE_DRIVE,
  TOOLKIT_PROVIDERS.SHAREPOINT,
  TOOLKIT_PROVIDERS.DROPBOX,
  TOOLKIT_PROVIDERS.OUTLOOK,
] as const;

/** Category values for reference-document filter chips (generate-document) */
const REF_DOC_CATEGORY = {
  ALL: 'ALL',
  WORKSPACE_KNOWLEDGE: 'WORKSPACE_KNOWLEDGE',
  PROJECT_KNOWLEDGE: 'PROJECT_KNOWLEDGE',
  DOCUMENTS: 'DOCUMENTS',
  FILES_FROM_LOCAL: 'FILES_FROM_LOCAL',
  INTEGRATIONS: 'INTEGRATIONS',
} as const;

const GENERATE_DOC_ATTACHMENT_CHIPS: AttachmentFilterChip[] = [
  { label: 'All', docModel: REF_DOC_CATEGORY.ALL },
  { label: 'Workspace Knowledge', docModel: REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE },
  { label: 'Project Knowledge', docModel: REF_DOC_CATEGORY.PROJECT_KNOWLEDGE },
  { label: 'Documents', docModel: REF_DOC_CATEGORY.DOCUMENTS },
  { label: 'Files from local', docModel: REF_DOC_CATEGORY.FILES_FROM_LOCAL },
  { label: 'Integrations', docModel: REF_DOC_CATEGORY.INTEGRATIONS },
];


@Component({
  selector: 'app-generate-document',
  standalone: true,
  imports: [FormsModule, CommonModule, ButtonModule, SelectModule, ProgressBarModule, RadioButtonModule, TabsModule, TooltipModule, PaginatorModule, ButtonComponent, SelectComponent, TabsPanelsComponent, SearchBarComponent, IconComponent, FileSelectionComponent, StepperComponent, StepperStepDirective, DatepickerComponent, InputComponent, MilestonesComponent, ShareToUsersComponent, AttachmentGroupComponent, MenuComponent, AssetUrlPipe, DocumentPreviewComponent, CheckboxComponent, DialogComponent],
  templateUrl: './generate-document.component.html',
  styleUrl: './generate-document.component.scss'
})
export class GenerateDocumentComponent implements OnInit, OnChanges, OnDestroy {



  @ViewChild('projectSelect') projectSelectRef?: SelectComponent;

  @Input() docTypeId: string = '';
  @Input() visible: boolean = false
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onDialogClose = new EventEmitter<void>();
  @Output() onGenerate = new EventEmitter<{ docTypeId: string }>();

  generateDocAttachmentChips = GENERATE_DOC_ATTACHMENT_CHIPS;

  docId!: string

  CASE_TYPE = CaseType;
  isAdmin = false;

  content: string = ''
  response: string = ''
  loading: boolean = false
  wordCount: number = 0
  wordLimit: number = 500
  docTypeLoading: boolean = false
  docTypes: IDocumentType[] = []
  docTypeOptions: SelectOption[] = []
  selectedDocTypeId: string = ''
  selectedDocType: IDocumentType | null = null
  get docTypeConfig(): SelectConfig {
    return {
      placeholder: 'Select a Document Type',
      searchable: true,
      clearable: true,
      disabled: this.loading,
      loading: this.docTypeLoading,
      appendTo: 'body',
      panelStyleClass: 'max-w-[40vw]'
    }
  }
  aiAssistant: {
    open: boolean,
    content: string,
    response: string,
    stage: string,
    loading: boolean
    loadingPercentage: number
  } = {
      open: false, content: '', response: '', stage: 'Igniting creativity â€“ Starting with AI', loading: false, loadingPercentage: 0
    };

  public promptQuestions = [
    {
      label: 'Generate a Lumpsum Contract',
      prompt: `Generate a [LUMP SUM CONTRACT] for [PROJECT NAME] between [OWNER / CLIENT NAME] and [CONTRACTOR NAME], using the following information: [PROJECT INFORMATION], [SCOPE OF WORK], [LUMP SUM PRICE], [PAYMENT TERMS], [PROJECT SCHEDULE / MILESTONES], [TERMS AND CONDITIONS], and [REFERENCE DOCUMENTS]. The contract shall reflect only the information provided, use professional contract language, and be complete, coherent, and ready for execution without introducing assumptions or external obligations.`
    },
    {
      label: 'Generate a Unit Rate Contract',
      prompt: `Generate a [UNIT RATE CONTRACT] for [PROJECT NAME] between [OWNER / CLIENT NAME] and [CONTRACTOR NAME], based on [PROJECT INFORMATION], [DESCRIPTION OF WORK], [UNIT RATES AND MEASUREMENT BASIS], [ESTIMATED QUANTITIES], [PAYMENT TERMS], [CONTRACT DURATION], [TERMS AND CONDITIONS], and [REFERENCE DOCUMENTS] provided. Use clear, professional contract language and include only obligations and provisions explicitly supported by the supplied information.`
    },
    {
      label: 'Generate a Time and Materials (T&M) Contract',
      prompt: `Generate a [TIME AND MATERIALS CONTRACT] for [PROJECT NAME] between [CLIENT NAME] and [SERVICE PROVIDER NAME], using [PROJECT INFORMATION], [DESCRIPTION OF SERVICES], [LABOR RATES], [MATERIAL MARKUPS], [PAYMENT TERMS], [NOT-TO-EXCEED AMOUNT, IF ANY], [CONTRACT TERM], [TERMS AND CONDITIONS], and [REFERENCE DOCUMENTS]. Ensure the contract is complete, consistent, and based solely on the information provided.`
    }
  ];

  public templateSuggestions: IFile[] = []

  templateSuggestionLoading: boolean = false
  selectedTemplate: IFile | null = null
  fromScratch: boolean = false
  templateSuggestionPage = false
  generationInProgress = true
  jobAlreadyInProgress = false

  activeLibraryTab = 'template';
  librarySearchValue = '';
  libraryTemplates: { _id: string; templateId?: string; name: string }[] = [];
  libraryClauses: { _id: string; templateId?: string; name: string }[] = [];
  libraryLoading = false;
  libraryFirst = 0;
  libraryRows = 10;
  libraryTotalRecords = 0;
  libraryRowsPerPageOptions = [10, 20, 30];
  selectedLibraryItemId: string | null = null;
  selectedLibraryClauseIds: string[] = [];
  libraryTabs: ITabPanelConfig[] = [
    { value: 'template', label: 'Template library' },
    { value: 'clause', label: 'Clause library' }
  ];
  selectedLibraryBundleId: string = ALL_BUNDLES_VALUE;
  clauseBundleOptions: SelectOption[] = [{ label: 'All', value: ALL_BUNDLES_VALUE }];
  libraryBundleSelectConfig: SelectConfig = {
    placeholder: 'Clause bundle',
    appendTo: 'body',
    styleClass: 'w-full'
  };

  referenceDocuments: { _id: string; name: string; docModel?: string; metadata?: Record<string, any> }[] = [];
  private localUploadedFiles: Map<string, File> = new Map();
  uploadDocumentsDialogVisible = false;
  parentReferenceDocument: FileItem | null = null;
  knowledgeBases: any[] = [];
  projects: any[] = [];
  fileSelectionDropdownOptions: DropdownOption[] = [];
  fileSelectionConfig: FileSelectionConfig = {
    maxTotalUploadedAttachments: 50,
    maxTotalUploadedFileSize: 500 * 1000 * 1000,
    localUploadMaxFileSize: 50 * 1000 * 1000,
    title: 'Upload files',
    showTotalAttachmentsCount: true,
    supportSubFolders: true,
    allowedFileTypes: [
      FileDropTypes.PDF,
      FileDropTypes.DOCX,
      FileDropTypes.DOC,
      FileDropTypes.XLSX,
      FileDropTypes.JPG,
      FileDropTypes.PNG,
      FileDropTypes.IMAGE,
      FileDropTypes.JPEG,
      FileDropTypes.MD,
      FileDropTypes.TEXT,
    ],
    tabs: [
      { id: QueryOptions.KNOWLEDGE_BASE, label: 'Workspace Knowledge', tooltip: 'Select from knowledge base files', type: TabType.SELECT, folderIcon: 'book-open-text', folderIconSource: 'lucide' },
      { id: QueryOptions.PROJECT_KNOWLEDGE, label: 'Project Knowledge', tooltip: 'Select from project knowledge', type: TabType.SELECT, folderIcon: 'book-open-text', folderIconSource: 'lucide' },
      { id: QueryOptions.PROJECT_FILES, label: 'Documents', tooltip: 'Select from home documents', type: TabType.SELECT, folderIcon: 'book-open-text', folderIconSource: 'lucide' },
      { id: QueryOptions.ADD_PHOTOS_AND_FILES, label: 'Files from local', tooltip: 'Select from local files', type: TabType.UPLOAD },
      { id: 'INTEGRATIONS', label: 'Integrations', tooltip: 'Select from integrations', type: TabType.SELECT, folderIcon: 'folder', folderIconSource: 'lucide' },
    ],
  };

  stepperVisible = false;
  readonly step1Config: StepperStepConfig = { id: 'libraries', label: 'Libraries', title: 'Libraries', subtitle: 'Choose a template or clause from the library', svg: GENERATE_WITH_AI_SVGS.LIBRARY_PLACEHOLDER };
  readonly step2Config: StepperStepConfig = {
    id: 'reference-documents',
    label: 'Reference documents',
    title: 'Reference documents',
    subtitle: 'Upload or select reference documents',
    svg: GENERATE_WITH_AI_SVGS.REFERENCE_DOC_PLACEHOLDER,
    isValid: () => this.hasReferenceDocuments(),
    invalidMessage: 'Please add at least one reference document',
  };
  readonly step3Config: StepperStepConfig = { id: 'customisations', label: 'Customization', title: 'Customization', subtitle: 'Set prompt, tone, project, due date and milestones', skipable: true, svg: GENERATE_WITH_AI_SVGS.CUSTOMIZATION_PLACEHOLDER };
  readonly step4Config: StepperStepConfig = { id: 'team', label: 'Team', title: 'Team', subtitle: 'Invite users to collaborate.', svg: GENERATE_WITH_AI_SVGS.SHARE_PLACEHOLDER };
  aiAssistantStepperConfig: StepperConfig = {
    subtitle: 'A completely new way to handle your construction documents',
    steps: [this.step1Config, this.step2Config, this.step3Config, this.step4Config],
    currentStepIndex: 0,
    dialogWidth: '80vw',
    dialogHeight: '90vh',
  };

  SVG_ICONS = GENERAL_AGENT_SVG;

  customisationPrompt = '';
  readonly toneOptions: CustomMenuItem[] = [

    {
      label: CHAT_STYLES.NORMAL,
      icon: this.SVG_ICONS.NORMAL_STYLE,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.NORMAL,
    },
    {
      label: CHAT_STYLES.LEARNING,
      icon: this.SVG_ICONS.LEARNING_STYLE,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.LEARNING,
    },
    {
      label: CHAT_STYLES.CONCISE,
      icon: this.SVG_ICONS.CONCISE_STYLE,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.CONCISE,
    },
    {
      label: CHAT_STYLES.FORMAL,
      icon: this.SVG_ICONS.FORMAL_STYLE,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.FORMAL,
    },
    {
      label: CHAT_STYLES.CASUAL,
      icon: this.SVG_ICONS.CASUAL_STYLE,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.CASUAL,
    },
    {
      label: CHAT_STYLES.EXPLANATORY,
      icon: this.SVG_ICONS.EXPLANATORY_STYLE,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.EXPLANATORY,
    },
    {
      label: CHAT_STYLES.BULLETS,
      icon: this.SVG_ICONS.BULLETS,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.BULLETS,
    },

    {
      label: CHAT_STYLES.SUMMARIZE,
      icon: this.SVG_ICONS.SUMMARIZE,
      iconSource: IconSourceEnum.SVG,
      value: CHAT_STYLES.SUMMARIZE,
    },

  ];
  selectedToneId = 'Normal';
  toneSelectConfig: SelectConfig = { placeholder: 'Select tone', searchable: false, clearable: true, appendTo: 'body' };
  projectOptions: SelectOption[] = [];
  selectedProjectId: string = '';
  private readonly projectsPageSize = 20;
  private projectsSkip = 0;
  private projectsTotal = 0;
  projectsLoading = false;
  dueDate: Date | null = null;
  dueDateConfig = { showIcon: true, dateFormat: 'dd/mm/yy' as const, placeholder: 'Select due date', appendTo: 'body' as const, minDate: new Date(), inputStyle: { padding: '0.45rem', fontSize: '0.875rem', paddingLeft: '1rem' } };
  activeCustomisationTab = 'customisations';
  customisationTabs: ITabPanelConfig[] = [
    { value: 'customisations', label: 'Customization' },
    { value: 'milestones', label: 'Contract milestones' }
  ];

  shareDetails: { users: IShareUser[]; anyone: SHARE_ACCESS; workspaceShare?: boolean; projectShare?: boolean } = {
    users: [],
    anyone: SHARE_ACCESS.RESTRICTED,
    workspaceShare: false,
    projectShare: false,
  };
  milestonePermissions = { canView: true, canEdit: true, canComment: true, restricted: false };

  get referenceDocumentsAsChatAttachments(): ChatAttachment[] {
    return this.referenceDocuments.map((d) => {
      const category = this.getReferenceDocCategory(d);
      return {
        _id: d._id,
        name: d.name,
        docModel: (d.docModel as DOCUMENT_MODEL) ?? DOCUMENT_MODEL.CONTRACT,
        type: getFileType(d.name) || FILE_TYPE.PDF,
        category,
      };
    });
  }

  private getReferenceDocCategory(d: { docModel?: string; metadata?: Record<string, any> }): string {
    if (d.metadata?.['connector'] && INTEGRATIONS_TOOLKIT_PROVIDERS.includes(d.metadata['connector'] as any)) {
      return REF_DOC_CATEGORY.INTEGRATIONS;
    }
    if (d.metadata?.['isProjectKnowledge']) return REF_DOC_CATEGORY.PROJECT_KNOWLEDGE;
    if (d.metadata?.['kbId']) return REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE;
    if (d.docModel === DOCUMENT_MODEL.CONTRACT || d.metadata?.['projectId']) return REF_DOC_CATEGORY.DOCUMENTS;
    if (d.docModel === DOCUMENT_MODEL.ATTACHMENT) return REF_DOC_CATEGORY.FILES_FROM_LOCAL;
    if (d.docModel === DOCUMENT_MODEL.REPOSITORY) return REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE;
    return REF_DOC_CATEGORY.FILES_FROM_LOCAL;
  }

  get toneMenuItems(): CustomMenuItem[] {
    return this.toneOptions;
  }
  get selectedToneMenuValues(): CustomMenuItem[] {
    const item = this.toneMenuItems.find((t) => t.value === this.selectedToneId);
    return item ? [item] : [];
  }
  toneMenuVisible = false;

  onToneSelected(item: CustomMenuItem): void {
    if (item.value !== undefined) {
      this.selectedToneId = item.value;
      this.toneMenuVisible = false;
    }
  }
  onProjectCleared(): void {
    this.selectedProjectId = '';
    this.shareDetails.projectShare = false;
  }
  get selectedToneLabel(): string {
    return this.toneOptions.find((o) => o.value === this.selectedToneId)?.label ?? 'Normal';
  }

  get selectedToneItem(): CustomMenuItem | undefined {
    return this.toneOptions.find((o) => o.value === this.selectedToneId);
  }

  onRemoveReferenceAttachment(attachment: ChatAttachment): void {
    this.removeReferenceDocument({ _id: attachment._id, name: attachment.name });
  }

  refDocSourceFn = (a: ChatAttachment): string => {
    switch (a.category) {
      case REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE: return 'Workspace Knowledge';
      case REF_DOC_CATEGORY.PROJECT_KNOWLEDGE: return 'Project Knowledge';
      case REF_DOC_CATEGORY.DOCUMENTS: return 'Documents';
      case REF_DOC_CATEGORY.FILES_FROM_LOCAL: return 'Files from local';
      case REF_DOC_CATEGORY.INTEGRATIONS: return 'Integrations';
      default: return 'Reference document';
    }
  };

  selectedTemplateId: string | null = null;
  showViewTemplateDialog: boolean = false;
  selectedTemplateAttachment: IAttachment = {} as IAttachment;

  milestones: IMilestone[] = [];
  editableMilestones: Array<Partial<IMilestone>> = []
  deletedMilestoneIds: string[] = []

  constructor(
    private toastService: ToastService,
    private contractService: ContractService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private documentTypeService: DocumentTypeService,
    private templateLibraryService: TemplateLibraryService,
    private knowledgeBaseService: KnowledgeBaseService,
    private projectService: ProjectService,
    private repositoryService: RepositoryService,
    private clauseLibraryService: ClauseLibraryService,
    private clauseBundleService: ClauseBundleService,
    private milestoneService: MileStoneService,
    private generalAgentService: GeneralAgentService,
    private integrationsService: IntegrationsService,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private userCreditsState: UserCreditsStateService,
  ) {
    this.docId = this.activatedRoute.snapshot.queryParams['parentDocId'] || this.activatedRoute.snapshot.queryParams['documentId'];

    this.route.queryParams.subscribe(params => {
      // status=success&connectedAccountId=ca_uM15Em3Q21mN&appName=gmail
      const status = params['status'] || '';
      const connectedAccountId = params['connectedAccountId'] || params['connected_account_id']
      const connectorToolkit = this.route.snapshot.firstChild?.params['connectorToolkit'] || this.route.snapshot.paramMap.get('connectorToolkit') || ''
      const appName = params['appName'] || connectorToolkit;
      if (status === 'success' && connectedAccountId && appName && connectorToolkit) {
        this.integrationsService.addConnector({
          toolKit: connectorToolkit as TOOLKIT_PROVIDERS,
          connectedAccountId: connectedAccountId,
          status: status,
          appName: appName
        }).subscribe((res: any) => {
          if (res.data.status) {
            this.toastService.success('Connection established successfully');
            (window.opener as any)?.['onConnectorConnected']?.();
            window.close();
          } else {
            this.toastService.error('Connection failed', 'Connection failed');
            window.close();
          }
        });
      }

    });
  }

  ngOnInit() {
    const currentUser = this.authenticationService.getUser();
    this.isAdmin = currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(currentUser.role) : false;
    this.getDocTypes();
    this.loadLibraryTemplates();
    this.loadProjectsForCustomisation();
    this.loadParentReferenceDocument();
    this.ensureDocumentOwnerInShareDetails();
    (window as any)['onConnectorConnected'] = () => this.getIntegrationsConnectors();

  }

  ngOnDestroy(): void {
    delete (window as any)['onConnectorConnected'];
  }

  private ensureDocumentOwnerInShareDetails(): void {
    const hasOwner = this.shareDetails.users.some((u) => u.role === SHARE_ACCESS.OWNER || (Array.isArray(u.role) && u.role.includes(SHARE_ACCESS.OWNER)));
    if (hasOwner) return;
    const currentUser = this.authenticationService.getUser();
    if (currentUser) {
      this.shareDetails.users = [...this.shareDetails.users, {
        name: currentUser.fullName ?? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' '),
        user: currentUser as IUser,
        email: currentUser.email as string,
        type: USER_TYPE.INTERNAL,
        role: SHARE_ACCESS.OWNER,
      } as IShareUser];
    }
  }

  loadProjectsForCustomisation(): void {
    this.projectsSkip = 0;
    this.projectsTotal = 0;
    this.projects = [];
    this.projectOptions = [];
    this.projectSelectRef?.setHasMore(true);
    this.fetchProjectsPage();
  }

  private fetchProjectsPage(): void {
    if (this.projectsLoading) return;
    this.projectsLoading = true;
    this.projectService.getProjects(this.projectsSkip, this.projectsPageSize).subscribe((res) => {
      this.projectsLoading = false;
      if (res?.success && res.data?.projects) {
        this.projectsTotal = res.data.totalRecords ?? 0;
        this.projectsSkip += res.data.projects.length;
        this.projects = [...this.projects, ...res.data.projects];
        const newOptions: SelectOption[] = res.data.projects.map((p: { _id: string; name: string }) => ({
          label: p.name,
          value: p._id,
          data: p,
        }));
        this.projectOptions = [...this.projectOptions, ...newOptions];
        this.projectSelectRef?.setHasMore(this.projectsSkip < this.projectsTotal);
      }
    });
  }

  onProjectLoadMore(): void {
    if (this.projectsSkip < this.projectsTotal) {
      this.fetchProjectsPage();
    } else {
      this.projectSelectRef?.setHasMore(false);
      this.projectSelectRef?.setLoading(false);
    }
  }

  onProjectSearch(event: { query: string }): void {
    const query = event.query.trim();
    if (!query) {
      this.loadProjectsForCustomisation();
      return;
    }
    this.projectsLoading = true;
    this.projectService.getProjects(0, 50, { searchValue: query }).subscribe((res) => {
      this.projectsLoading = false;
      if (res?.success && res.data?.projects) {
        this.projectsSkip = 0;
        this.projectsTotal = 0;
        this.projectOptions = res.data.projects.map((p: { _id: string; name: string }) => ({
          label: p.name,
          value: p._id,
          data: p,
        }));
        this.projectSelectRef?.setHasMore(false);
      }
    });
  }

  private loadParentReferenceDocument(): void {
    if (!this.docId) return;
    this.contractService.fetchContract(this.docId).subscribe({
      next: (res: any) => {
        const doc = res?.data ?? res;
        if (!doc?._id) return;
        const dropdownId = doc.project?._id || doc.project || 'all';
        const projectName = doc.project?.name || 'All documents';
        this.parentReferenceDocument = {
          id: doc._id,
          name: doc.name || 'Document',
          metadata: {
            tabId: QueryOptions.PROJECT_FILES,
            dropdownId,
            projectId: dropdownId,
            projectName,
            docModel: DOCUMENT_MODEL.CONTRACT,
          },
        };
        this.syncFileSelectionInitialFiles();
      },
      error: () => {
        this.parentReferenceDocument = null;
      }
    });
  }

  getDataFromStepper(): IGenerateWithAIBody {
    const template = this.activeLibraryTab === 'template' ? (this.selectedLibraryItemId ?? '') : '';
    const clauses = this.activeLibraryTab === 'clause' ? this.selectedLibraryClauseIds : [];
    const shareUsers = this.shareDetails.users?.length
      ? this.shareDetails.users.map((u) => ({
        email: u.email,
        name: u.name,
        user: (u.user as any)?._id ?? (typeof u.user === 'string' ? u.user : undefined),
        role: Array.isArray(u.role) ? u.role : u.role ? [u.role] : [SHARE_ACCESS.VIEW],
      }))
      : undefined;
    const milestones = this.editableMilestones?.length
      ? this.editableMilestones.map((m) => ({ name: m.name, date: m.date, reminder: m.reminder }))
      : undefined;
    return {
      prompt: this.customisationPrompt || '',
      docTypeId: this.selectedDocType?._id ?? '',
      template,
      clauses,
      tone: this.selectedToneItem?.value || CHAT_STYLES.NORMAL,
      dueDate: this.dueDate ? this.dueDate.toISOString() : '',
      customPrompt: this.customisationPrompt || '',
      referenceDocuments: this.referenceDocuments.map((d) => ({
        docModel: d.docModel ?? DOCUMENT_MODEL.CONTRACT,
        name: d.name,
        id: d._id,
        ...(d.metadata && { metadata: d.metadata }),
      })),
      project: this.selectedProjectId || '',
      parentDocId: this.docId,
      shareUsers,
      milestones,
      source: this.selectedDocType?.name === 'CONTRACT' ? DOCUMENT_CREATION_SOURCE.CONTRACT_GENERATION : DOCUMENT_CREATION_SOURCE.STANDARD,
    };
  }

  startGeneration(body: IGenerateWithAIBody): void {
    const agentRemaining = this.userCreditsState.agentRemaining();
    if (agentRemaining <= 0) {
      this.toastService.error("You've run out of credits. Request more to continue.");
      return;
    }
    if (agentRemaining < 3) {
      this.toastService.warn(`Low credits (${agentRemaining} remaining). This task may not complete.`);
    }
    this.aiAssistant.loading = true;
    const stages = [
      'Igniting creativity â€“ Starting with AI!',
      'Setting the foundation â€“ Preparing your document.',
      'AI is on it â€“ your document is taking shape!',
      'Enhancing details â€“ Refining the context.',
      'Reviewing for consistency â€“ Preparing the final version.',
      'Finalizing details - almost there!'
    ];
    let stageIndex = 0;
    const interval = setInterval(() => {
      if (stageIndex < stages.length) {
        this.aiAssistant.stage = stages[stageIndex];
        this.aiAssistant.loadingPercentage = Math.min((stageIndex + 1) * 15, 95);
        stageIndex++;
      } else {
        clearInterval(interval);
      }
    }, 3000);
    this.toastService.info('The document is being generated.');

    const formData = new FormData();
    formData.append('prompt', body.prompt);
    formData.append('docTypeId', body.docTypeId);
    formData.append('tone', body.tone);
    formData.append('customPrompt', body.customPrompt);
    formData.append('source', body.source);
    if (body.template) formData.append('template', body.template as string);
    if (body.parentDocId) formData.append('parentDocId', body.parentDocId);
    if (body.project) formData.append('project', body.project);
    if (body.dueDate) formData.append('dueDate', body.dueDate);
    if (body.linkedDocId) formData.append('linkedDocId', body.linkedDocId);
    formData.append('clauses', JSON.stringify(body.clauses ?? []));
    formData.append('shareUsers', JSON.stringify(body.shareUsers ?? []));
    formData.append('milestones', JSON.stringify(body.milestones ?? []));
    const systemRefs = body.referenceDocuments.filter(d => !d.id.startsWith('local-'));
    formData.append('referenceDocuments', JSON.stringify(systemRefs));
    this.localUploadedFiles.forEach((file) => formData.append('uploadedFiles', file));
    this.contractService.generateContractWithAi(formData).subscribe({
      next: (res: any) => {
        clearInterval(interval);
        this.aiAssistant.loading = false;
        if (res.success) {
          this.stepperVisible = false;
          this.visible = false;
          this.visibleChange.emit(false);
          this.onDialogClose.emit();
          this.onGenerate.emit({ docTypeId: this.selectedDocType?._id ?? '' });
          this.cancel();
          this.reset();
        } else {
          this.toastService.error('Error generating document');
        }
      }, error: () => {
        clearInterval(interval);
        this.aiAssistant.loading = false;
        this.toastService.error('Error generating document');
      }
    });
  }

  reset(): void {
    this.selectedLibraryItemId = null;
    this.selectedLibraryClauseIds = [];
    this.selectedLibraryBundleId = ALL_BUNDLES_VALUE;
    this.librarySearchValue = '';
    this.libraryFirst = 0;
    this.libraryRows = 10;
    this.activeLibraryTab = 'template';
    this.referenceDocuments = [];
    this.localUploadedFiles.clear();
    this.uploadDocumentsDialogVisible = false;
    this.syncFileSelectionInitialFiles();
    this.customisationPrompt = '';
    this.selectedToneId = 'normal';
    this.selectedProjectId = '';
    this.dueDate = null;
    this.activeCustomisationTab = 'customisations';
    this.milestones = [];
    this.editableMilestones = [];
    this.deletedMilestoneIds = [];
    this.shareDetails = {
      users: [],
      anyone: SHARE_ACCESS.RESTRICTED,
      workspaceShare: false,
      projectShare: false,
    };
    const currentUser = this.authenticationService.getUser();
    if (currentUser) {
      this.shareDetails.users.push({
        name: currentUser.fullName ?? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' '),
        user: currentUser as IUser,
        email: currentUser.email as string,
        type: USER_TYPE.INTERNAL,
        role: SHARE_ACCESS.OWNER,
      } as IShareUser);
    }
    this.aiAssistantStepperConfig = {
      ...this.aiAssistantStepperConfig,
      currentStepIndex: 0,
    };
    this.showViewTemplateDialog = false;
    this.openClausePreviewDialog = false;
    this.selectedTemplateId = null;
    this.selectedTemplate = null;
    this.selectedTemplateAttachment = {} as IAttachment;
    this.selectedClause = null;
  }

  onStepperFinish(): void {
    const body = this.getDataFromStepper();
    this.startGeneration(body);
  }

  onStepperClose(): void {
    this.stepperVisible = false;
    this.reset();
    this.cancel();
  }

  onShareUpdateUser(user: IShareUser): void {
    const idx = this.shareDetails.users.findIndex((u) => u.email === user.email);
    if (idx !== -1) {
      this.shareDetails.users = this.shareDetails.users.slice();
      this.shareDetails.users[idx] = user;
    }
  }

  onRemoveUserFromShare(user: IShareUser | Partial<IShareUser>): void {
    const email = user?.email;
    if (email) {
      this.shareDetails.users = this.shareDetails.users.filter((u) => u.email !== email);
    }
  }

  onMilestonesChange(milestones: Partial<IMilestone>[]): void {
    this.editableMilestones = milestones.map((m) => ({
      ...m,
      date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
      reminder: m.reminder
    }));
    this.deletedMilestoneIds = [];
  }

  getTemplates(filters?: { skip?: number; limit?: number; search?: string }) {
    return this.templateLibraryService.getTemplates({
      skip: filters?.skip ?? 0,
      limit: filters?.limit ?? 50,
      search: filters?.search || undefined
    });
  }

  loadLibraryTemplates(): void {
    this.libraryLoading = true;
    this.templateLibraryService.getTemplates({
      search: this.librarySearchValue || undefined,
      skip: this.libraryFirst,
      limit: this.libraryRows,
      documentType: this.docTypeId
    }).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res;
        this.libraryTemplates = Array.isArray(payload?.data) ? payload.data : [];
        this.libraryTotalRecords = payload?.total ?? 0;
        this.libraryLoading = false;
      },
      error: () => {
        this.libraryLoading = false;
      }
    });
  }

  loadLibraryClauses(): void {
    this.libraryLoading = true;
    const filters: any = {
      status: CLAUSE_STATUS.APPROVED,
      searchValue: this.librarySearchValue,
      skip: this.libraryFirst,
      limit: this.libraryRows
    };
    const bundleId = this.selectedLibraryBundleId?.trim();
    if (bundleId && bundleId !== ALL_BUNDLES_VALUE) {
      filters.clauseBundle = [bundleId];
    }
    this.clauseLibraryService.getClauses(filters).subscribe({
      next: (res: any) => {
        this.libraryLoading = false;
        if (res.success && res.data) {
          this.libraryClauses = res.data.clauses ?? [];
          this.libraryTotalRecords = res.data.total ?? 0;
        }
      },
      error: () => {
        this.libraryLoading = false;
      }
    });
  }

  onCustomisationTabChange(event: { value: string; tab: ITabPanelConfig }): void {
    this.activeCustomisationTab = event.value;
  }

  onLibraryTabChange(event: { value: string; tab: ITabPanelConfig }): void {
    this.activeLibraryTab = event.value;
    this.selectedLibraryItemId = null;
    this.selectedLibraryClauseIds = [];
    this.libraryFirst = 0;
    if (event.value === 'template') {
      this.loadLibraryTemplates();
    } else {
      this.getClauseBundles();
      this.loadLibraryClauses();
    }
  }

  getClauseBundles(): void {
    this.clauseBundleService.getBundles({}, false).subscribe({
      next: (res: any) => {
        if (res?.data?.bundles) {
          this.clauseBundleOptions = [
            { label: 'All', value: ALL_BUNDLES_VALUE },
            ...(res.data.bundles as IClauseBundle[]).map((b: IClauseBundle) => ({ label: b.name, value: b._id }))
          ];
        }
      }
    });
  }

  onLibraryBundleChange(bundleId: string): void {
    this.selectedLibraryBundleId = bundleId;
    this.libraryFirst = 0;
    this.loadLibraryClauses();
  }

  onLibrarySearchChange(): void {
    this.libraryFirst = 0;
    if (this.activeLibraryTab === 'template') {
      this.loadLibraryTemplates();
    } else {
      this.loadLibraryClauses();
    }
  }

  onLibraryPageChange(event: PaginatorState): void {
    this.libraryFirst = event.first ?? 0;
    this.libraryRows = event.rows ?? this.libraryRows;
    if (this.activeLibraryTab === 'template') {
      this.loadLibraryTemplates();
    } else {
      this.loadLibraryClauses();
    }
  }

  selectLibraryItem(id: string): void {
    if (this.activeLibraryTab === 'template') {
      this.selectedLibraryItemId = this.selectedLibraryItemId === id ? null : id;
    } else {
      const idx = this.selectedLibraryClauseIds.indexOf(id);
      if (idx === -1) {
        this.selectedLibraryClauseIds = [...this.selectedLibraryClauseIds, id];
      } else {
        this.selectedLibraryClauseIds = this.selectedLibraryClauseIds.filter((x) => x !== id);
      }
    }
  }

  onTemplateRadioClick(event: Event, itemId: string): void {
    if (this.selectedLibraryItemId === itemId) {
      this.selectedLibraryItemId = null;
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.stopPropagation();
    }
  }

  get allClausesSelected(): boolean {
    if (this.displayLibraryItems.length === 0) return false;
    return this.displayLibraryItems.every(item => this.selectedLibraryClauseIds.includes(item._id));
  }

  get someClausesSelected(): boolean {
    return this.activeLibraryTab === 'clause' && this.selectedLibraryClauseIds.length > 0 && !this.allClausesSelected;
  }

  onToggleSelectAllClauses(checked: boolean): void {
    const currentPageIds = this.displayLibraryItems.map((item) => item._id);

    if (checked) {
      const newIds = currentPageIds.filter(id => !this.selectedLibraryClauseIds.includes(id));
      this.selectedLibraryClauseIds = [...this.selectedLibraryClauseIds, ...newIds];
    } else {
      this.selectedLibraryClauseIds = this.selectedLibraryClauseIds.filter(id => !currentPageIds.includes(id));
    }
  }

  get displayLibraryItems(): { _id: string; templateId?: string; name: string }[] {
    return this.activeLibraryTab === 'template' ? this.libraryTemplates : this.libraryClauses;
  }

  onNewTemplate(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onDialogClose.emit();
    window.open(
      this.router.serializeUrl(
        this.router.createUrlTree(['/settings/library/template-library'])
      ),
      '_blank'
    );
  }
  onNewClause(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onDialogClose.emit();
    window.open(
      this.router.serializeUrl(
        this.router.createUrlTree(['/settings/library/clause-library'])
      ),
      '_blank'
    );
  }

  openClausePreviewDialog: boolean = false;
  selectedClause: IClause | null = null;
  onViewLibraryItem(_item: { _id: string; templateId?: string; name: string }): void {
    if (this.activeLibraryTab === 'template') {
      this.templateLibraryService.getTemplate(_item._id).subscribe({
        next: (res: any) => {
          if (res.success && res.data) {
            this.selectedTemplate = res.data
            this.selectedTemplateAttachment = res.data.attachment as IAttachment
            this.showViewTemplateDialog = true
            this.selectedTemplateId = _item._id;
          } else {
            this.toastService.error('Failed to load template', 'error');
          }
        },
        error: () => {
          this.toastService.error('Failed to load template', 'error');
        }
      });
    }
    if (this.activeLibraryTab === 'clause') {
      this.selectedClause = this.libraryClauses.find((c) => c._id === _item._id) as IClause ?? null;
      if (this.selectedClause) {
        console.log(this.selectedClause);
        this.openClausePreviewDialog = true;
      }
    }

  }


  private hasReferenceDocuments(): boolean {
    return this.referenceDocuments.length > 0 || !!this.parentReferenceDocument;
  }

  openUploadDocumentsDialog(): void {
    this.loadKnowledgeBasesForFileSelection();
    this.loadProjectsForDropdown();
    this.getIntegrationsConnectors();
    this.syncFileSelectionInitialFiles();
    this.uploadDocumentsDialogVisible = true;
  }

  private syncFileSelectionInitialFiles(): void {
    const initialSelectedFilesById = new Map<string, FileItem>();

    this.referenceDocuments.forEach((d) => {
      initialSelectedFilesById.set(d._id, {
        id: d._id,
        name: d.name,
        metadata: {
          ...(d.metadata ?? {}),
          docModel: d.docModel ?? d.metadata?.['docModel'],
        },
      });
    });

    if (this.parentReferenceDocument) {
      initialSelectedFilesById.set(this.parentReferenceDocument.id, this.parentReferenceDocument);
    }

    this.fileSelectionConfig = {
      ...this.fileSelectionConfig,
      initialSelectedFiles: Array.from(initialSelectedFilesById.values()),
    };
  }

  loadProjectsForDropdown(): void {
    this.projectService.getProjects(0, 1000).subscribe((res) => {
      if (res?.success) {
        this.projects = res.data?.projects ?? [];
      }
    });
  }

  prepareFilesDropdownOptions(): void {
    const options: DropdownOption[] = [
      { id: 'all', label: 'All files', groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' },
    ];
    this.knowledgeBases.forEach((kb) => {
      options.push({
        id: kb._id,
        label: kb.name,
        value: kb,
        groupKey: 'metadata.kbId',
        groupLabel: 'metadata.kbName',
      });
    });
    this.fileSelectionDropdownOptions = options;
  }

  prepareDocumentsDropdownOptions(): void {
    const options: DropdownOption[] = [
      { id: 'all', label: 'All documents', groupKey: 'metadata.projectId', groupLabel: 'metadata.projectName' },
    ];
    this.projects.forEach((project) => {
      options.push({
        id: project._id,
        label: project.name,
        value: project,
        groupKey: 'metadata.projectId',
        groupLabel: 'metadata.projectName',
      });
    });
    this.fileSelectionDropdownOptions = options;
  }

  prepareProjectKnowledgeDropdownOptions(): void {
    const options: DropdownOption[] = [];
    this.projects.forEach((project) => {
      options.push({
        id: project._id,
        label: project.name,
        value: project,
        groupKey: 'metadata.kbId',
        groupLabel: 'metadata.breadcrumbLabel',
      });
    });
    this.fileSelectionDropdownOptions = options;
  }

  private readonly manageConnectorsMenuItem: CustomMenuItem = {
    label: 'Manage Connectors',
    value: 'manage-connectors',
    icon: 'settings-2',
    type: MenuItemType.SINGLE_SELECT,
    iconSource: IconSourceEnum.LUCIDE,
    subOptions: [],
    buttonCallback: () => {
      this.router.navigate(['/settings/integrations']);
    },
  };

  integrationsConnectorsMenuItems: CustomMenuItem[] = [this.manageConnectorsMenuItem];

  integrationPaginationState: { [key: string]: { nextPageToken?: string; nextCursor?: string } } = {};
  private outlookMailsCache = new Map<string, MailMessageInfo>();

  private fetchOutlookMails(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    const connector = TOOLKIT_PROVIDERS.OUTLOOK;
    if (event.parentFolderId) {
      const mail = this.outlookMailsCache.get(event.parentFolderId);
      if (mail) {
        const items: FileItem[] = (mail.attachments ?? []).map((a) => ({
          id: a.id,
          name: a.name ?? 'Attachment',
          size: a.size,
          isFolder: false,
          metadata: { connector, messageId: mail.id, attachmentId: a.id, subject: mail.subject, contentType: a.contentType },
        }));
        return of({ items, totalRecords: items.length });
      }
      return of({ items: [], totalRecords: 0 });
    }
    const pageSize = event.pageSize || 20;
    const key = `${event.tabId}:${connector}:root`;
    const saved = this.integrationPaginationState[key];
    const params: any = { connector, pageSize };
    if (event.page > 0 && saved?.nextPageToken) params.pageToken = saved.nextPageToken;
    return this.integrationsService.listMails(params).pipe(
      map((res: any) => {
        const mails: MailMessageInfo[] = res?.data?.data ?? [];
        mails.forEach((mail) => this.outlookMailsCache.set(mail.id, mail));
        const items: FileItem[] = mails
          .filter((mail) => mail.hasAttachments)
          .map((mail) => ({
            id: mail.id,
            name: mail.subject ?? 'No Subject',
            isFolder: true,
            folderIcon: 'mail',
            folderIconSource: 'lucide',
            metadata: { connector, messageId: mail.id, subject: mail.subject },
          }));
        const nextPageToken = res?.data?.nextPageToken;
        if (nextPageToken) this.integrationPaginationState[key] = { nextPageToken };
        return { items, totalRecords: items.length + (res?.data?.hasMore ? pageSize : 0) };
      }),
      catchError(() => of({ items: [], totalRecords: 0 }))
    );
  }

  private normalizeIntegrationListResponse(
    connector: string,
    data: any,
    pageSize: number,
    siteUrl?: string
  ): { items: FileItem[]; nextPageToken?: string; nextCursor?: string; hasMore?: boolean } {
    const items: FileItem[] = [];
    let rawList: any[] = [];
    if (Array.isArray(data)) rawList = data;
    else if (data?.files && Array.isArray(data.files)) rawList = data.files;
    else if (data?.entries && Array.isArray(data.entries)) rawList = data.entries;
    else if (data?.value && Array.isArray(data.value)) rawList = data.value;
    else if (data?.children && Array.isArray(data.children)) rawList = data.children;
    for (const entry of rawList) {
      const isFolder = entry.mimeType === 'application/vnd.google-apps.folder' || entry.folder != null || entry['.tag'] === 'folder' || entry.file?.isFolder || entry.type === 'folder';
      const name = entry.name ?? entry.path_display?.split('/').pop() ?? entry.path_display ?? entry.file?.name ?? 'Unknown';
      let id: string;
      let metadata: Record<string, unknown> = { connector };
      if (connector === TOOLKIT_PROVIDERS.GOOGLE_DRIVE) {
        id = entry.id ?? '';
        metadata = { ...metadata, fileId: entry.id, mime_type: entry.mimeType };
      } else if (connector === TOOLKIT_PROVIDERS.DROPBOX) {
        id = entry.path_lower ?? entry.path_display ?? entry.id ?? '';
        metadata = { ...metadata, path: entry.path_lower ?? entry.path_display };
      } else if (connector === TOOLKIT_PROVIDERS.ONE_DRIVE) {
        id = this.buildOneDrivePath(entry);
        metadata = { ...metadata, fileId: entry.id, file_name: entry.name };
      } else if (connector === TOOLKIT_PROVIDERS.SHAREPOINT) {
        const relativeUrl = entry.serverRelativeUrl ?? entry.id ?? entry.path ?? '';
        id = isFolder && siteUrl ? `${siteUrl}|||${relativeUrl}` : relativeUrl;
        const baseUrl = siteUrl ? siteUrl.replace(/\/+$/, '') : undefined;
        metadata = { ...metadata, serverRelativeUrl: relativeUrl, path: entry.path ?? relativeUrl, ...(baseUrl ? { siteUrl: baseUrl } : {}) };
      } else {
        id = entry.id ?? entry.path ?? entry.serverRelativeUrl ?? '';
        metadata = { ...metadata, fileId: entry.id, path: entry.path, serverRelativeUrl: entry.serverRelativeUrl };
      }
      items.push({
        id,
        name,
        size: entry.size ?? entry.file?.size,
        isFolder: !!isFolder,
        metadata: { ...metadata, connector },
      });
    }
    const nextPageToken = data?.nextPageToken ?? (data as any)?.['@odata.nextLink'];
    const nextCursor = data?.cursor;
    const hasMore = !!nextPageToken || !!nextCursor || (data?.has_more === true);
    return { items, nextPageToken, nextCursor, hasMore };
  }

  private buildOneDrivePath(entry: any): string {
    const parentPath = entry.parentReference?.path || '/drive/root:';
    const cleanParent = parentPath.replace('/drive/root:', '');
    if (!cleanParent) return `/${entry.name}`;
    return `${cleanParent}/${entry.name}`;
  }

  private normalizeSharepointSites(data: any): { items: FileItem[]; nextPageToken?: string; nextCursor?: string; hasMore?: boolean } {
    const rawList: any[] = Array.isArray(data) ? data : [];
    const items: FileItem[] = rawList.map((site: any) => ({
      id: `${site.WebId}|||${site.Path ?? site.SiteName ?? ''}`,
      name: site.Title ?? 'Unknown Site',
      isFolder: true,
      metadata: {
        connector: TOOLKIT_PROVIDERS.SHAREPOINT,
        siteUrl: site.Path ?? site.SiteName,
        siteId: site.SiteId,
        webId: site.WebId,
        isSite: true,
      },
    }));
    return { items, hasMore: false };
  }

  fetchIntegrationsFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    const connector = event.dropdownId ?? '';
    const status = this.getIntegrationConnectorToolkitStatus(connector);
    if (status === 'null') {
      this.toastService.warn('Connect your account from the Connectors menu to view files.');
      return of({ items: [], totalRecords: 0 });
    }
    if (status === 'disabled') {
      this.toastService.warn('Enable this connector from the Connectors menu to view files.');
      return of({ items: [], totalRecords: 0 });
    }
    if (status !== 'connected') {
      return of({ items: [], totalRecords: 0 });
    }
    if (connector === TOOLKIT_PROVIDERS.OUTLOOK) return this.fetchOutlookMails(event);
    const pageSize = event.pageSize || 20;
    const key = `${event.tabId}:${connector}:${event.parentFolderId ?? 'root'}`;
    const saved = this.integrationPaginationState[key];
    const params: any = { connector, pageSize, page: event.page === 0 ? 1 : event.page + 1 };
    let fullSiteUrl: string | undefined;
    if (event.parentFolderId) {
      if (connector === TOOLKIT_PROVIDERS.GOOGLE_DRIVE) {
        params.folderId = event.parentFolderId;
      } else if (connector === TOOLKIT_PROVIDERS.ONE_DRIVE) {
        params.folderPath = event.parentFolderId;
      } else if (connector === TOOLKIT_PROVIDERS.DROPBOX) {
        params.folderPath = event.parentFolderId;
      } else if (connector === TOOLKIT_PROVIDERS.SHAREPOINT) {
        if (event.parentFolderId.includes('|||')) {
          const [firstPart, secondPart] = event.parentFolderId.split('|||', 2);
          if (firstPart.startsWith('http')) {
            // Subfolder within a site: siteUrl|||serverRelativeUrl
            fullSiteUrl = firstPart;
            params.siteUrl = firstPart;
            params.serverRelativeUrl = secondPart;
          } else {
            // Site root: webId|||siteUrl
            fullSiteUrl = secondPart;
            params.siteUrl = secondPart;
          }
        } else {
          params.serverRelativeUrl = event.parentFolderId;
        }
      }
    }
    if (event.page > 0 && saved) {
      if (saved.nextPageToken) params.pageToken = saved.nextPageToken;
      if (saved.nextCursor) params.cursor = saved.nextCursor;
    }
    return this.integrationsService.listFiles(params).pipe(
      map((res: any) => {
        const payload = res?.data?.data ?? res;
        const dataKind = res?.data?.dataKind;
        const normalized =
          connector === TOOLKIT_PROVIDERS.SHAREPOINT && dataKind === 'sites'
            ? this.normalizeSharepointSites(payload)
            : this.normalizeIntegrationListResponse(connector, payload, pageSize, fullSiteUrl);

        const nextPageToken = res?.data?.hasMore ? res?.data?.nextPageToken : normalized.nextPageToken;
        const nextCursor = res?.data?.nextCursor ?? normalized.nextCursor;
        if (nextPageToken || nextCursor) {
          this.integrationPaginationState[key] = { nextPageToken, nextCursor };
        }
        const totalRecords = normalized.items.length + (res?.data?.hasMore ? pageSize : 0);
        return { items: normalized.items, totalRecords };
      }),
      catchError(() => of({ items: [], totalRecords: 0 }))
    );
  }

  getIntegrationsConnectors(): void {
    this.integrationsService.getConnectors().subscribe((res: any) => {
      if (res?.success && res.data) {
        const integrationMapping: { [key: string]: { name: string; icon: string } } = CONNECTOR_PROVIDER_INFO;
        const allowedSet = new Set<string>(INTEGRATIONS_TOOLKIT_PROVIDERS);
        const connectorMenuItems: CustomMenuItem[] = [];
        for (const category of res.data) {
          if (category?.values && Array.isArray(category.values)) {
            for (const connector of category.values) {
              if (!allowedSet.has(connector.name) || !integrationMapping[connector.name]) continue;
              const mapping = integrationMapping[connector.name];
              let menuItem: CustomMenuItem = {
                label: mapping.name,
                value: connector.name,
                icon: mapping.icon,
                iconSource: IconSourceEnum.SVG,
                subOptions: [],
                class: '[&>svg]:w-4 [&>svg]:h-4',
              };
              if (connector.toolkit === true) {
                menuItem = { ...menuItem, type: MenuItemType.TOGGLE, toggleState: true };
              } else if (connector.toolkit === false) {
                menuItem = { ...menuItem, type: MenuItemType.TOGGLE, toggleState: false };
              } else if (connector.toolkit === null) {
                menuItem = {
                  ...menuItem,
                  type: MenuItemType.BUTTON,
                  buttonLabel: 'Connect',
                  buttonClass: 'theme-text border-none',
                };
              }
              connectorMenuItems.push(menuItem);
            }
          }
        }
        this.integrationsConnectorsMenuItems = [...connectorMenuItems, this.manageConnectorsMenuItem];
      }
    });
  }

  prepareIntegrationsDropdownOptions(): void {
    const options: DropdownOption[] = INTEGRATIONS_TOOLKIT_PROVIDERS.map((provider) => {
      const info = CONNECTOR_PROVIDER_INFO[provider];
      return { id: provider, label: info.name, value: provider, icon: info.icon, iconSource: IconSourceEnum.SVG, iconSize: 'lg', iconClass: '[&>svg]:w-4 [&>svg]:h-4' };
    });
    this.fileSelectionDropdownOptions = options;
  }

  prepareFileSelectionDropdownOptions(): void {
    const options: DropdownOption[] = [{ id: 'all', label: 'All files', groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' }];
    this.knowledgeBases.forEach((kb) => {
      options.push({ id: kb._id, label: kb.name, value: kb, groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' });
    });
    this.fileSelectionDropdownOptions = options;
  }


  loadKnowledgeBasesForFileSelection(): void {
    this.knowledgeBaseService.getKnowledgeBaseStore({ skip: 0, limit: 1000, scope: KNOWLEDGE_SCOPE.WORKSPACE }).subscribe((res) => {
      if (res?.success && res?.data?.stores) {
        this.knowledgeBases = res.data.stores;
        this.prepareFileSelectionDropdownOptions();
      }
    });
  }

  getIntegrationConnectorConnected(connectorId: string | null): boolean {
    if (!connectorId) return false;
    const item = this.integrationsConnectorsMenuItems.find((m) => m.value === connectorId);
    return item?.type === MenuItemType.TOGGLE && item?.toggleState === true;
  }

  getIntegrationConnectorToolkitStatus(connectorId: string | null): 'connected' | 'null' | 'disabled' | null {
    if (!connectorId) return null;
    const item = this.integrationsConnectorsMenuItems.find((m) => m.value === connectorId);
    if (!item) return null;
    if (item.type === MenuItemType.BUTTON) return 'null';
    if (item.type === MenuItemType.TOGGLE) return item.toggleState === true ? 'connected' : 'disabled';
    return null;
  }

  onIntegrationsConnectorSelect(option: CustomMenuItem): void {
    if (option.value === 'manage-connectors') {
      this.router.navigate(['/settings/integrations']);
      return;
    }
    const toolKitValue = option.value as TOOLKIT_PROVIDERS;
    if (option.type === MenuItemType.BUTTON) {
      option.buttonLoading = true;
      option.disabled = true;
      option.buttonLabel = '';
      this.integrationsService.addConnector({ toolKit: toolKitValue, callbackUrl: 'explore/callback' }).subscribe((res: any) => {
        if (res?.data?.status) {
          this.toastService.success('Connection initiated successfully');
          if (res.data.redirectUrl) {
            window.open(res.data.redirectUrl, '_blank');
          }
        } else {
          this.toastService.error('Connection failed', 'Connection failed');
          option.buttonLoading = false;
          option.disabled = false;
          option.buttonLabel = 'Connect';
        }
        this.getIntegrationsConnectors();
      });
      return;
    }
    if (option.toggleState) {
      this.integrationsService.enableConnector(toolKitValue).subscribe((res: any) => {
        if (res?.data) {
          this.toastService.success('Connection enabled successfully');
        } else {
          this.toastService.error('Connection enable failed');
        }
        this.getIntegrationsConnectors();
      });
    } else {
      this.integrationsService.disableConnector(toolKitValue).subscribe((res: any) => {
        if (res?.data) {
          this.toastService.success('Connection disabled successfully');
        } else {
          this.toastService.error('Connection disable failed');
        }
        this.getIntegrationsConnectors();
      });
    }
  }

  fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent) => {
    if (event.tabId === QueryOptions.KNOWLEDGE_BASE) {
      if (event.dropdownId === 'all') {
        return this.fetchAllKnowledgeBaseFiles(event);
      }
      if (event.dropdownId) {
        return this.fetchKnowledgeBaseFiles(event.dropdownId, event);
      }
      this.fileSelectionDropdownOptions = [];
      this.prepareFileSelectionDropdownOptions();
      return of({ items: [], totalRecords: 0 });
    }
    if (event.tabId === QueryOptions.PROJECT_FILES) {
      if (event.dropdownId === 'all') {
        return this.fetchAllProjectDocuments(event);
      }
      if (event.dropdownId) {
        return this.fetchProjectDocuments(event.dropdownId, event);
      }
      this.fileSelectionDropdownOptions = [];
      this.prepareDocumentsDropdownOptions();
      return of({ items: [], totalRecords: 0 });
    }
    if (event.tabId === QueryOptions.PROJECT_KNOWLEDGE) {
      if (event.dropdownId === 'all') {
        return this.fetchAllProjectKnowledgeFiles(event);
      }
      if (event.dropdownId) {
        return this.fetchProjectKnowledgeFiles(event.dropdownId, event);
      }
      this.fileSelectionDropdownOptions = [];
      this.prepareProjectKnowledgeDropdownOptions();
      return of({ items: [], totalRecords: 0 });
    }
    if (event.tabId === INTEGRATIONS_TAB_ID) {
      this.prepareIntegrationsDropdownOptions();
      if (event.dropdownId && INTEGRATIONS_TOOLKIT_PROVIDERS.includes(event.dropdownId as (typeof INTEGRATIONS_TOOLKIT_PROVIDERS)[number])) {
        return this.fetchIntegrationsFiles(event);
      }
      return of({ items: [], totalRecords: 0 });
    }
    return of({ items: [], totalRecords: 0 });
  };

  private fetchAllKnowledgeBaseFiles(event: FileSelectionChangeEvent) {
    return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.WORKSPACE }).pipe(
      map((res) => {
        const allFiles: FileItem[] = [];
        const files = (res?.data || []).filter((f: { type: string }) => f.type === 'FILE');
        const filtered = event.searchQuery
          ? files.filter((f: { name?: string }) => (f.name || '').toLowerCase().includes((event.searchQuery || '').toLowerCase()))
          : files;
        filtered.forEach((file: any) => {
          (file.knowledgeBases || []).forEach((kb: { _id: string }) => {
            const kbDetails = this.knowledgeBases.find((k) => k._id === kb._id);
            allFiles.push({
              id: file._id,
              name: file.name || 'Unknown',
              size: file.size,
              metadata: { kbId: kbDetails?._id, kbName: kbDetails?.name, originalFile: file },
            });
          });
          if (!file.knowledgeBases?.length) {
            allFiles.push({
              id: file._id,
              name: file.name || 'Unknown',
              size: file.size,
              metadata: { kbId: 'file-repository', kbName: 'Files', originalFile: file },
            });
          }
        });
        const start = event.page * event.pageSize;
        const page = allFiles.slice(start, start + event.pageSize);
        return { items: page, totalRecords: allFiles.length };
      })
    );
  }

  private fetchKnowledgeBaseFiles(kbId: string, event: FileSelectionChangeEvent) {
    const kb = this.knowledgeBases.find((k) => k._id === kbId);
    if (!kb?.files) return of({ items: [], totalRecords: 0 });
    let items: FileItem[] = kb.files
      .filter((f: any) => !event.searchQuery || (f.file?.name || '').toLowerCase().includes((event.searchQuery || '').toLowerCase()))
      .map((f: any) => ({
        id: f.file?._id,
        name: f.file?.name || 'Unknown',
        size: f.file?.size,
        metadata: { kbId: kb._id, kbName: kb.name, originalFile: f.file },
      }));
    const start = event.page * event.pageSize;
    return of({ items: items.slice(start, start + event.pageSize), totalRecords: items.length });
  }


  fetchAllProjectDocuments(event: FileSelectionChangeEvent) {
    this.prepareDocumentsDropdownOptions();
    const skip = event.page * event.pageSize;
    const limit = event.pageSize;
    return this.contractService
      .getContracts({
        type: CONTRACT_TYPE.ALL,
        skip,
        limit,
        name: event.searchQuery || '',
      })
      .pipe(
        map((res) => {
          const documents: FileItem[] = [];
          let totalRecords = 0;
          if (res?.success && res.data?.contracts) {
            res.data.contracts.forEach((doc: any) => {
              const project = this.projects.find((p) => p._id === (doc.projectId || doc.project?._id));
              const projectId = doc.projectId || doc.project?._id || 'no-project';
              const projectName = project?.name || doc.project?.name || 'No project';
              documents.push({
                id: doc._id,
                name: doc.name || 'Unknown',
                size: doc?.fileSize ?? 0,
                metadata: {
                  projectId,
                  projectName,
                  originalDoc: doc,
                },
              });
            });
            totalRecords = res.data.total ?? 0;
          }
          return { items: documents, totalRecords };
        })
      );
  }

  fetchProjectDocuments(projectId: string, event: FileSelectionChangeEvent) {
    this.prepareDocumentsDropdownOptions();
    const project = this.projects.find((p) => p._id === projectId);
    if (!project) return of({ items: [], totalRecords: 0 });
    return this.contractService
      .getContracts({
        type: CONTRACT_TYPE.ALL,
        skip: event.page * event.pageSize,
        limit: event.pageSize,
        name: event.searchQuery || '',
        projectId,
      })
      .pipe(
        map((res) => {
          if (res?.success && res.data?.contracts) {
            const documents: FileItem[] = res.data.contracts.map((doc: any) => ({
              id: doc._id,
              name: doc.name || 'Unknown',
              metadata: { projectId: project._id, projectName: project.name, originalDoc: doc },
            }));
            return { items: documents, totalRecords: res.data.total ?? 0 };
          }
          return { items: [], totalRecords: 0 };
        })
      );
  }

  fetchAllProjectKnowledgeFiles(event: FileSelectionChangeEvent) {
    this.prepareProjectKnowledgeDropdownOptions();
    if (event.parentFolderId) {
      return this.repositoryService.getFolderDetails(event.parentFolderId).pipe(
        map((res: any) => {
          const childs = res?.data?.childs ?? res?.childs ?? [];
          const filtered = childs.filter((file: any) =>
            !event.searchQuery || file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())
          );
          const allItems: FileItem[] = filtered.map((file: any) => {
            if (file.type === 'FOLDER') {
              return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
            }
            const projectId = file.project?._id ?? file.project ?? 'no-project';
            const projectName = file.project?.name ?? 'No project';
            const project = this.projects.find((p) => p._id === projectId);
            return {
              id: file._id,
              name: file.name || 'Unknown',
              size: file.size,
              metadata: {
                projectId: project?._id ?? projectId,
                projectName: project?.name ?? projectName,
                originalFile: file,
                isProjectKnowledge: true,
              },
            };
          });
          const start = event.page * event.pageSize;
          const end = start + event.pageSize;
          return { items: allItems.slice(start, end), totalRecords: allItems.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }
    return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT }).pipe(
      map((res) => {
        const raw = (res as any)?.data ?? [];
        const filtered = raw.filter((file: any) =>
          !event.searchQuery || file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())
        );
        const allFiles: FileItem[] = [];
        filtered.forEach((file: any) => {
          if (file.type === 'FOLDER') {
            allFiles.push({ id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} });
          } else {
            const projectId = file.project?._id ?? file.project ?? 'no-project';
            const projectName = file.project?.name ?? 'No project';
            const project = this.projects.find((p) => p._id === projectId);
            allFiles.push({
              id: file._id,
              name: file.name || 'Unknown',
              size: file.size,
              metadata: {
                projectId: project?._id ?? projectId,
                projectName: project?.name ?? projectName,
                originalFile: file,
                isProjectKnowledge: true,
              },
            });
          }
        });
        const start = event.page * event.pageSize;
        const end = start + event.pageSize;
        return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
      })
    );
  }

  fetchProjectKnowledgeFiles(projectId: string, event: FileSelectionChangeEvent) {
    this.prepareProjectKnowledgeDropdownOptions();
    const project = this.projects.find((p) => p._id === projectId);
    if (!project) return of({ items: [], totalRecords: 0 });
    const allKnowledgeFolderId = ALL_KNOWLEDGE_FOLDER_PREFIX + projectId;
    if (event.parentFolderId === allKnowledgeFolderId) {
      return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId }).pipe(
        map((res: any) => {
          const raw = res?.data ?? [];
          const filtered = raw.filter((file: any) =>
            !event.searchQuery || file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())
          );
          const allFiles: FileItem[] = filtered
            .filter((f: any) => f.type === 'FILE')
            .map((file: any) => ({
              id: file._id,
              name: file.name || 'Unknown',
              size: file.size,
              metadata: {
                projectId: project._id,
                projectName: project.name,
                originalFile: file,
                isProjectKnowledge: true,
              },
            }));
          const start = event.page * event.pageSize;
          const end = start + event.pageSize;
          return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }
    if (event.parentFolderId) {
      return this.knowledgeBaseService.getKnowledgeBaseStoreById(event.parentFolderId).pipe(
        map((res: any) => {
          const store = res?.data ?? res;
          if (!store?.files) return { items: [], totalRecords: 0 };
          const projectName = project.name;
          const kbName = store.name || 'Unknown';
          const breadcrumbLabel = `${projectName} > ${kbName}`;
          let list = store.files.map((item: any) => ({
            id: item.file?._id,
            name: item.file?.name || 'Unknown',
            size: item.file?.size,
            metadata: {
              projectId: project._id,
              projectName,
              kbId: store._id,
              kbName,
              breadcrumbLabel,
              originalFile: item.file,
              isProjectKnowledge: true,
            },
          }));
          if (event.searchQuery) {
            const q = event.searchQuery.toLowerCase();
            list = list.filter((f: FileItem) => f.name?.toLowerCase().includes(q));
          }
          const start = event.page * event.pageSize;
          const end = start + event.pageSize;
          return { items: list.slice(start, end), totalRecords: list.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }
    return this.knowledgeBaseService.getKnowledgeBaseStore({
      scope: KNOWLEDGE_SCOPE.PROJECT,
      project: projectId,
      skip: event.page === 0 ? 0 : event.page * event.pageSize - 1,
      limit: event.page === 0 ? event.pageSize - 1 : event.pageSize,
      search: event.searchQuery || '',
    }).pipe(
      map((res) => {
        const stores = res?.data?.stores ?? [];
        const totalStores = res?.data?.total ?? 0;
        const storeItems: FileItem[] = stores.map((store: any) => ({
          id: store._id,
          name: store.name || 'Unknown',
          isFolder: true,
          metadata: { kbId: store._id, kbName: store.name },
        }));
        const allKnowledgeFolder: FileItem = {
          id: allKnowledgeFolderId,
          name: 'All Knowledge',
          isFolder: true,
          metadata: {},
        };
        const items: FileItem[] = event.page === 0 ? [allKnowledgeFolder, ...storeItems] : storeItems;
        const totalRecords = totalStores + 1;
        return { items, totalRecords };
      }),
      catchError(() => of({ items: [], totalRecords: 0 }))
    );
  }

  private buildIntegrationMetadata(file: FileItem, connector: string): Record<string, any> {
    const meta: Record<string, any> = {
      connector,
      file_name: file.name,
      mime_type: file.metadata?.mime_type,
    };
    if (connector === 'googledrive' || connector === 'one_drive') meta['fileId'] = file.metadata?.['fileId'] ?? file.id;
    else if (connector === 'dropbox') meta['path'] = file.metadata?.['path'] ?? file.id;
    else if (connector === 'share_point') {
      const siteUrl = (file.metadata?.['siteUrl'] ?? '').toString().replace(/\/+$/, '');
      const serverRelativeUrl = file.metadata?.['serverRelativeUrl'] ?? file.metadata?.['path'] ?? file.id;
      meta['siteUrl'] = siteUrl;
      meta['serverRelativeUrl'] = serverRelativeUrl;
    }
    else meta['fileId'] = file.id;
    return meta;
  }

  onFileSelectionSubmit(event: FileSelectionSubmitEvent): void {
    const newRefs: { _id: string; name: string; docModel?: string; metadata?: Record<string, any> }[] = [];
    const docModelFromTab = (tabId: string, dropdownId?: string) => {
      if (tabId === INTEGRATIONS_TAB_ID && dropdownId) return dropdownId;
      if (tabId === QueryOptions.KNOWLEDGE_BASE || tabId === QueryOptions.PROJECT_KNOWLEDGE) return DOCUMENT_MODEL.REPOSITORY;
      if (tabId === QueryOptions.PROJECT_FILES) return DOCUMENT_MODEL.CONTRACT;
      return DOCUMENT_MODEL.ATTACHMENT;
    };
    const docModelFromFile = (file: FileItem) => {
      if (file.metadata?.connector && INTEGRATIONS_TOOLKIT_PROVIDERS.includes(file.metadata.connector as any)) return file.metadata.connector as string;
      if (file.metadata?.isProjectKnowledge || file.metadata?.kbId) return DOCUMENT_MODEL.REPOSITORY;
      if (file.metadata?.projectId) return DOCUMENT_MODEL.CONTRACT;
      return DOCUMENT_MODEL.ATTACHMENT;
    };
    if (event.selectedFiles?.length) {
      event.selectedFiles.forEach((group) => {
        const docModel = docModelFromTab(group.tabId, group.dropdownId ?? undefined);
        group.files.forEach((file) => {
          if (!file.metadata?.isAttachment) {
            const isIntegration = group.tabId === INTEGRATIONS_TAB_ID && group.dropdownId;
            const _id = file.id;
            const metadata = isIntegration ? this.buildIntegrationMetadata(file, group.dropdownId!) : file.metadata ? { ...file.metadata } : undefined;
            newRefs.push({ _id, name: file.name, docModel, ...(metadata && { metadata }) });
          }
        });
      });
    } else {
      const selected = event.allSelectedFiles ?? [];
      selected.forEach((file) => {
        if (!file.metadata?.isAttachment) {
          const docModel = docModelFromFile(file);
          const isIntegration = INTEGRATIONS_TOOLKIT_PROVIDERS.includes(docModel as any);
          const _id = file.id;
          const metadata = isIntegration ? this.buildIntegrationMetadata(file, docModel) : file.metadata ? { ...file.metadata } : undefined;
          newRefs.push({ _id, name: file.name, docModel, ...(metadata && { metadata }) });
        }
      });
    }
    (event.uploadedFiles ?? []).forEach((file, index) => {
      const tempId = `local-${Date.now()}-${index}`;
      newRefs.push({ _id: file.name, name: file.name, docModel: DOCUMENT_MODEL.ATTACHMENT });
      this.localUploadedFiles.set(tempId, file);
    });
    const existingIds = new Set(this.referenceDocuments.map((d) => d._id));
    newRefs.forEach((ref) => {
      if (!existingIds.has(ref._id)) {
        this.referenceDocuments.push(ref);
        existingIds.add(ref._id);
      }
    });
    this.uploadDocumentsDialogVisible = false;
    this.syncFileSelectionInitialFiles();
  }

  onFileSelectionCancel(): void {
    this.uploadDocumentsDialogVisible = false;
  }

  removeReferenceDocument(doc: { _id: string; name: string }): void {
    this.referenceDocuments = this.referenceDocuments.filter((d) => d._id !== doc._id);
    this.localUploadedFiles.delete(doc._id);
    this.syncFileSelectionInitialFiles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.jobAlreadyInProgress = false;
      this.checkJobInProgress();
      this.loadLibraryTemplates();
      if (this.activeLibraryTab === 'clause') {
        this.loadLibraryClauses();
      }
    }
  }

  openAIAssistant() {
    this.visible = true;
  }

  cancel() {
    this.resetAiAssistant();
    this.templateSuggestionPage = false
    this.visibleChange.emit(this.visible);
    this.onDialogClose.emit();
  }

  resetAiAssistant() {
    this.content = '';
    this.response = '';
    this.loading = false;
    this.aiAssistant.loading = false;
    this.aiAssistant.stage = 'Igniting creativity - Starting with AI!';
    this.aiAssistant.loadingPercentage = 0;
    this.jobAlreadyInProgress = false;
  }

  checkJobInProgress() {
    this.generationInProgress = true;
    const source = this.selectedDocType?.name === 'CONTRACT' ? DOCUMENT_CREATION_SOURCE.CONTRACT_GENERATION : DOCUMENT_CREATION_SOURCE.STANDARD;
    this.contractService.checkGenWithAiInProgress(source).subscribe({
      next: res => {
        if (res && res.data) {
          this.jobAlreadyInProgress = true;
        }
        this.generationInProgress = false;
      },
      error: err => {
        this.generationInProgress = false;
      }
    });
  }

  goToGenerateWithAiAndClose() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onDialogClose.emit();
    this.jobAlreadyInProgress = false;
    this.resetAiAssistant();
    this.templateSuggestionPage = false;
    this.router.navigate(['/explore/generate-with-ai'], { state: { generationStarted: true } });
  }

  getDocTypes() {
    this.docTypeLoading = true
    this.documentTypeService.getDocTypes().subscribe(res => {
      if (res && res.success) {
        this.docTypeLoading = false
        this.docTypes = res.data || []
        this.docTypeOptions = this.docTypes.map(docType => ({
          label: docType.name,
          value: docType._id,
          data: docType
        }))
        this.getMilestones();
      }
      else {
        this.docTypeLoading = false
      }
    })
  }

  handleDocTypeChange(event: any) {
    this.selectedDocTypeId = event.value
    this.selectedDocType = this.docTypes.find(dt => dt._id === event.value) || null
  }
  selectTemplate(template: IFile) {
    this.selectedTemplate = template;
  }

  getRelatedTemplates() {
    this.templateSuggestionPage = true;
    this.aiAssistant.stage = 'Setting the foundation â€“ Choose a template to start.';
    this.templateSuggestionLoading = true;
    this.contractService.getRelatedTemplates(this.content, this.selectedDocType?._id || '').subscribe(
      {
        next: res => {
          if (res) {
            this.templateSuggestions = res.data;
            this.templateSuggestionLoading = false;
          }
        },
        error: err => {
          console.error(err)
          this.templateSuggestionLoading = false;
        }
      });
  }

  checkWordLimit() {
    this.wordCount = this.getWordCount(this.content);

    if (this.wordCount > this.wordLimit) {
      const wordsArray = this.content.split(/\s+/).slice(0, this.wordLimit);
      this.content = wordsArray.join(' ');
    }
  }

  openDocument(documentId: string) {
    window.location.href = `/edit-document?documentId=${documentId}`;
  }

  getWordCount(content: string): number {
    if (!content) return 0;
    return content.trim().split(/\s+/).length;
  }


  getMilestones() {
    this.selectedDocType = this.docTypes.find(docType => docType.name === 'CONTRACT') ?? null;
    this.milestoneService.getMilestones(MILESTONE_TYPE.DOCUMENT, undefined, undefined, this.selectedDocType?._id ?? '').subscribe(res => {
      if (res.success) {
        this.milestones = res.data || [];
        this.editableMilestones = this.milestones.map(m => ({
          ...m,
          date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
          reminder: m.reminder
        }));
        this.deletedMilestoneIds = [];
      }
    });
  }


  sendPrompt(prompt: string) {
    this.content = prompt;
    this.customisationPrompt = prompt;
    this.checkWordLimit();
  }

}
